/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCaseMergeActions } from "../../../api/case-dedup/useCaseMergeActions";
import { useManualMerge } from "../../../api/case-dedup/useManualMerge";
import CustomModal from "../../Common/CustomModal/CustomModal";
import CaseCard from "./CaseCard";
import NewMergeCase from "./demo/NewMergeCase";

const CaseDuplicateData = ({ caseRecords }) => {
  const [selectedCases, setSelectedCases] = useState([]);
  const [mergedFinalData, setMergedFinalData] = useState({});
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [selectedSecondary, setSelectedSecondary] = useState([]);
  const [selectAll, setSelectAll] = useState(false); // NEW

  const { handleManualMerge, isMerging } = useManualMerge();
  const {
    handleSeparate: handleSeparateAPI,
    handleAutoMerge: handleAutoMergeAPI,
    isLoading,
    error,
  } = useCaseMergeActions();
  const navigate = useNavigate();

  const {
    onOpen: onNewMergeModalOpen,
    isOpen: isNewMergeModalOpen,
    onClose: onNewMergeModalClose,
  } = useDisclosure();

  useEffect(() => {
    if (caseRecords.length > 0 && !selectedPrimary) {
      setSelectedPrimary(caseRecords[0]);
      setSelectedCases([caseRecords[0]]);
    }
  }, [caseRecords, selectedPrimary]);

  useEffect(() => {
    if (selectedPrimary) {
      const initialData = {};
      for (const [key, value] of Object.entries(selectedPrimary)) {
        if (!Array.isArray(value) && value !== "" && value != null) {
          initialData[key] = value;
        }
      }
      setMergedFinalData(initialData);
    }
  }, [selectedPrimary]);

  const handleSelectionChange = (profile, isChecked) => {
    setSelectedCases((prevSelected) => {
      if (profile.id === caseRecords[0].id) {
        return prevSelected; // Prevent deselecting first card
      }
      const updated = isChecked
        ? [...prevSelected, profile]
        : prevSelected.filter((p) => p.id !== profile.id);

      // Update selectAll state if needed
      if (updated.length === caseRecords.length) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }

      return updated;
    });
  };

  const handleSelectAllChange = (isChecked) => {
    setSelectAll(isChecked);

    if (isChecked) {
      // Select all except the first (already selected by default)
      const allCases = [
        caseRecords[0],
        ...caseRecords.slice(1), // include all others
      ];
      setSelectedCases(allCases);
    } else {
      // Deselect all except first
      setSelectedCases([caseRecords[0]]);
    }
  };

  // ==============apis==========================
  const handleManualMerege = async () => {
    try {
      if (!selectedPrimary) {
        toast.error("Please select a primary case.");
        return;
      }

      const cleanedSecondaries = selectedCases.filter(
        (c) => c.id !== selectedPrimary.id
      );

      const result = await handleManualMerge(
        { ...mergedFinalData, id: selectedPrimary.id },
        cleanedSecondaries
      );

      if (result.success) {
        toast.success("Case merged successfully");
        onNewMergeModalClose();
        navigate(-1);
      } else {
        toast.error("Case merge failed: " + result.error);
        onNewMergeModalClose();
      }
    } catch (err) {
      toast.error("Unexpected error: " + err.message);
      onNewMergeModalClose();
    }
  };

  const handleSeparate = async () => {
    const { success, error } = await handleSeparateAPI(selectedCases);
    if (success) {
      toast.success("Case separated successfully.");
      navigate(-1);
    } else {
      toast.error(error || "Failed to separate case.");
    }
  };

  // In CaseDuplicateData component
  const handleAutoMerge = async () => {
    if (!selectedPrimary) {
      toast.error("Please select a primary case.");
      return;
    }
    const secondaries = selectedCases.filter(
      (c) => c.id !== selectedPrimary.id
    );
    if (secondaries.length === 0) {
      toast.error("Please select at least one secondary case to merge.");
      return;
    }
    const { success, error } = await handleAutoMergeAPI(
      selectedPrimary,
      secondaries
    );

    if (success) {
      toast.success("Case merged successfully.");
      navigate(-1);
    } else {
      toast.error(error || "Failed to auto merge.");
    }
  };

  // =====================apis===================

  return (
    <Box m={4}>
      <Flex alignItems="center" gap={2} justify="space-between" mb={1}>
        <Box>
          <Button
            onClick={() => navigate(-1)}
            size="sm"
            leftIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
        <Box>
          <Button
            disabled={selectedCases?.length <= 0}
            size="sm"
            mr={2}
            onClick={handleSeparate}
          >
            Keep Separate
          </Button>
          <Button
            disabled={!selectedPrimary || selectedCases.length < 2}
            size="sm"
            mr={2}
            onClick={handleAutoMerge}
          >
            Auto Merge
          </Button>
          <Button
            size="sm"
            mr={2}
            onClick={onNewMergeModalOpen}
            disabled={selectedCases?.length < 2}
          >
            Manual Merge
          </Button>
        </Box>
      </Flex>

      {/* Select All Checkbox */}

      <Box my={2}>
        <Flex mb={2} gap={5}>
          <Text fontSize="xl" fontWeight={500}>
            Basic Case Information
          </Text>
          <Checkbox
            isChecked={selectAll}
            onChange={(e) => handleSelectAllChange(e.target.checked)}
          >
            Select All
          </Checkbox>
        </Flex>
        <SimpleGrid columns={4} spacing={4}>
          {caseRecords?.map((item, index) => {
            return (
              <Box key={item.id}>
                <CaseCard
                  item={item}
                  onSelectionChange={handleSelectionChange}
                  isSelected={selectedCases.some((p) => p.id === item.id)}
                  isFirst={index === 0}
                  selectedPrimary={selectedPrimary}
                  setSelectedPrimary={setSelectedPrimary}
                  selectedSecondary={selectedSecondary}
                  setSelectedSecondary={setSelectedSecondary}
                />
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      <CustomModal
        size="6xl"
        isCentered={true}
        title="Merge Case"
        isOpen={isNewMergeModalOpen}
        onClose={onNewMergeModalClose}
        bodyContent={
          <Box h="100%">
            <NewMergeCase
              data={selectedCases}
              setMergedFinalData={setMergedFinalData}
              mergedFinalData={mergedFinalData}
            />
          </Box>
        }
        footerContent={
          <Flex alignItems="center" justify="space-between">
            <Flex gap={5}>
              <Button colorScheme="blackAlpha" size="sm" w="100px">
                Back
              </Button>
              <Button
                colorScheme="green"
                size="sm"
                w="100px"
                onClick={handleManualMerege}
                isLoading={isMerging}
                loadingText="merging...."
              >
                Merge
              </Button>
            </Flex>
          </Flex>
        }
      />
    </Box>
  );
};

export default CaseDuplicateData;
